import {
  type EditAction,
  movableRootsOf,
  reshapableRootOf,
  reshapeActions,
  selectionBounds,
  transformActions,
} from '@/application';
import { type Reshape, type Similarity, translation } from '@/domain/construction';
import { angle, type Vec2, vec } from '@/domain/math';
import { type Entity, type EntityId } from '@/domain/model';
import { type TransformBox } from '@/rendering';

import { snap, type SnapResult } from '../Snapping';

import {
  exceedsDragThreshold,
  type PointerInput,
  type Tool,
  type ToolContext,
  type ToolPrompt,
  type ToolResponse,
} from './Tool';
import {
  boxOf,
  containsPoint,
  cursorFor,
  frameAfter,
  frameOf,
  geometryFor,
  Handle,
  headingOf,
  hitHandle,
  isUsable,
  orientedFrame,
  reshapeFromDrag,
  RESHAPE_HANDLES,
  similarityFromDrag,
  SIMILARITY_HANDLES,
  type TransformFrame,
} from './transformBox';

/**
 * Moving, turning and resizing a selection by dragging a frame around it.
 *
 * ## Why this exists, given that `TransformPanel` deliberately does not
 *
 * The panel's argument against handles is recorded and it is a good one: *"dragging a corner
 * cannot say ninety degrees; it can only say about ninety, and a figure that is nearly rotated
 * by a right angle is a figure that quietly teaches the wrong thing."*
 *
 * This does not overturn that argument, it satisfies it. A turn here **snaps to fifteen
 * degrees** and the number it produces is exact, not near — the pointer chooses which multiple,
 * not what the multiple is. The figure the drag settles on is written beside the cursor while
 * it happens, so the exactness is visible rather than merely claimed. The primary modifier
 * escapes the snap for the rare case that wants an arbitrary angle, which is the same escape
 * the rest of the editor gives snapping.
 *
 * The panel stays. A user who knows they want ninety degrees should be able to say so without
 * touching the drawing, and a user finding the shape by eye should be able to drag. These are
 * different acts and the editor should be able to do both.
 *
 * ## Why a mode rather than part of the selection tool
 *
 * Handles sit on and outside the frame, which means over empty space and over other entities.
 * Folding them into `SelectTool` would let a grip that happens to lie on another shape's vertex
 * steal the click meant for that shape, and would stop a press just outside the frame starting
 * a marquee. `SelectTool` already carries five states and three special cases; this is where
 * that file would stop being reviewable.
 *
 * It is also a mode in the user's head. Pressing the key does not change what is selected, it
 * changes what dragging *means* — which is exactly what Photoshop's free transform is, and
 * exactly what a separate tool models.
 */

/** What the pointer took hold of. `body` is the inside of the frame, which moves it. */
type Grip = Handle | 'body';

/**
 * What a drag from this grip will do, decided once at press time.
 *
 * Fixed for the gesture rather than re-derived per event. Whether a per-axis stretch is on offer
 * depends on the selection, and a selection that changed mid-drag would otherwise turn a stretch
 * into a uniform resize halfway through — one gesture with two meanings and, because the merge
 * key would have to change with it, two undo steps.
 */
type Gesture = 'move' | 'similarity' | 'reshape';

interface Gripped {
  readonly grip: Grip;
  readonly gesture: Gesture;
  readonly origin: Vec2;
  /**
   * The frame as it was when the gesture began.
   *
   * Frozen, and that is the whole reason the tool holds one. The drag is measured against this:
   * recomputing it as the figure moves would move the anchor away from the pointer that is
   * pulling on it, and the figure would run away from the cursor.
   */
  readonly frame: TransformFrame;
  readonly before: ReadonlyMap<EntityId, Entity>;
  readonly mergeKey: string;
}

type State =
  | { readonly name: 'idle' }
  | ({ readonly name: 'pressing' } & Gripped)
  | ({ readonly name: 'gesturing' } & Gripped);

export class TransformTool implements Tool {
  readonly id = 'transform';
  readonly label = 'Biến đổi tự do';
  readonly shortcut = 'b';
  readonly group = 'Vẽ';

  /**
   * A transform rewrites coordinates and adds nothing, so it is a change of appearance rather
   * than of structure — which is what exploring a construction is allowed to do. The controller
   * already agrees: `blocksInExploreMode` lets `update-entity` through. Without this flag the
   * tool would be unreachable there while its edits stayed legal, which is a rule enforced in
   * the wrong place.
   */
  readonly restructures = false;

  private state: State = { name: 'idle' };

  /* ------------------------------------------------------------------- the frame */

  /**
   * The frame the tool is working with, or `null` when there is nothing to work on.
   *
   * ## Why an upright frame is recomputed and a turned one is carried
   *
   * `selectionBounds` returns an axis-aligned box. While the frame is upright that box *is* the
   * frame, so recomputing it every time is not merely safe but better: the frame then tracks
   * whatever else the drawing does — an undo, a vertex dragged with the selection tool, a value
   * typed into a panel — with no staleness to manage.
   *
   * The moment the frame is turned, the two part company: the upright box round a figure at 45°
   * is √2 larger than the figure. Recomputing then would make the frame jump outward on release
   * and shrink back on the next turn, so from that point on it is carried, and each committed
   * gesture moves it.
   *
   * Mid-gesture the frozen one always wins, whichever kind it is.
   */
  private frameFor(context: ToolContext): TransformFrame | null {
    if (this.state.name !== 'idle') return this.state.frame;

    /*
     * A shape that records its own orientation is authoritative about its own frame, and reading
     * it needs none of the machinery below: there is nothing to carry and nothing to go stale,
     * because the source *is* the frame. It also has to win, since the upright box round a
     * turned ellipse is larger than the ellipse and at a different angle to it — and only the
     * ellipse's own box can be stretched without shearing it.
     */
    const own = this.ownFrame(context);
    if (own !== null) return isUsable(own) ? own : null;

    const key = selectionKey(context.selection);
    if (this.turned !== null && this.turnedFor === key) return this.turned;

    const box = selectionBounds(context.scene.values, context.selection);
    if (box === null) return null;

    const frame = frameOf(box);
    return isUsable(frame) ? frame : null;
  }

  /** The frame a single shape states for itself, when it states one. */
  private ownFrame(context: ToolContext): TransformFrame | null {
    const only = reshapableRootOf(context.document, context.selection);
    if (only === null) return null;

    const source = context.document.entities.get(only)?.source;
    if (source?.op !== 'free-ellipse') return null;

    return orientedFrame(source.center, source.rotation, source.rx * 2, source.ry * 2);
  }

  /** Set only once a gesture has turned the frame; see `frameFor`. */
  private turned: TransformFrame | null = null;
  private turnedFor: string | null = null;

  /** Carries the frame forward past a committed gesture. */
  private noteFrame(context: ToolContext, frame: TransformFrame, transform: Similarity): void {
    const next = frameAfter(frame, transform);
    const upright = Math.abs(headingOf(next)) < 1e-9;

    if (upright && this.turned === null) return; // bounds will say the same thing
    this.turned = next;
    this.turnedFor = selectionKey(context.selection);
  }

  /* ------------------------------------------------------------------- the gesture */

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;

    const frame = this.frameFor(context);
    if (frame === null) return null;

    const grip = this.gripAt(context, frame, input.world);
    if (grip === null) {
      /*
       * Outside the frame. Photoshop commits and leaves the mode on a click outside, and the
       * expectation carries: a user who has finished transforming reaches for the drawing
       * rather than for a key. Handing back the selection tool is what "finished" means here —
       * nothing is pending, so there is nothing to commit.
       */
      return { finished: true };
    }

    const before = this.snapshot(context);
    if (before === null) return null;

    const gesture = this.gestureFor(context, grip);

    this.state = {
      name: 'pressing',
      grip,
      gesture,
      origin: input.world,
      frame,
      before,
      // Fixed for the whole gesture. A key that changed partway would split one drag into two
      // undo steps at the moment it changed.
      mergeKey: `transform:${grip}:${[...before.keys()].sort().join(',')}`,
    };

    return { cursor: 'grabbing' };
  }

  onPointerMove(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (this.state.name === 'idle') return this.hoverResponse(context, input);

    if (this.state.name === 'pressing') {
      // Below the threshold this is still a click, not a drag, and nothing should have moved.
      if (!exceedsDragThreshold(this.state.origin, input.world, context.zoom)) return null;
      this.state = { ...this.state, name: 'gesturing' };
    }

    return this.gestureResponse(context, input);
  }

  /**
   * The cursor over a grip, before anything is pressed.
   *
   * The one piece of feedback that tells a user a frame *has* grips, and which way each one
   * pulls. Turned with the frame, because a grip on a figure rotated by 45° pulls diagonally
   * however the frame chooses to name it.
   */
  private hoverResponse(context: ToolContext, input: PointerInput): ToolResponse | null {
    const frame = this.frameFor(context);
    if (frame === null) return null;

    const handle = hitHandle(
      frame,
      input.world,
      geometryFor(context.zoom),
      this.handlesFor(context)
    );
    if (handle !== null) return { cursor: cursorFor(frame, handle) };

    return { cursor: containsPoint(frame, input.world) ? 'move' : 'default' };
  }

  onPointerUp(context: ToolContext, input: PointerInput): ToolResponse | null {
    switch (this.state.name) {
      case 'idle':
        return null;

      case 'pressing':
        this.state = { name: 'idle' };
        this.readout = null;
        return { cursor: 'default' };

      case 'gesturing': {
        const { frame, gesture } = this.state;
        const landed = this.snapped(context, this.state, input)?.world ?? input.world;
        const transform = this.transformFor(this.state, landed, input);
        const response = this.gestureResponse(context, input);

        this.state = { name: 'idle' };
        this.readout = null;

        /*
         * Only a similarity is carried forward. A reshape acts on a shape that states its own
         * frame, which `ownFrame` reads back from the source — so there is nothing to remember,
         * and remembering would only give the reading something to disagree with.
         */
        if (transform !== null && gesture !== 'reshape') {
          this.noteFrame(context, frame, transform as Similarity);
        }

        /*
         * Sealing is what separates two successive drags into two undo steps. They share a
         * merge key by design — that is how four hundred pointer events become one — so only
         * the end of the gesture can tell the history that one thing has finished.
         */
        return { ...response, preview: [], cursor: 'default', sealHistory: true };
      }
    }
  }

  /**
   * Puts the figure back where it was and ends the gesture.
   *
   * Returns the restoring edits rather than merely forgetting, because a half-finished
   * transform left on the drawing is not a cancellation. The snapshot holds whole entities
   * rather than sources so that this can be answered without a context, which `cancel` is not
   * given.
   */
  cancel(): ToolResponse | null {
    if (this.state.name === 'idle') return null;

    const { before } = this.state;
    this.state = { name: 'idle' };
    this.readout = null;

    const actions: EditAction[] = [...before.values()].map((entity) => ({
      kind: 'update-entity',
      entity,
    }));

    return { actions, preview: [], cursor: 'default', sealHistory: true };
  }

  /** Forgets the gesture without restoring anything. Idempotent, and called after `cancel`. */
  reset(): void {
    this.state = { name: 'idle' };
    this.readout = null;
  }

  prompt(): ToolPrompt | null {
    if (this.state.name !== 'idle') return null;
    return {
      tool: this.label,
      step: 0,
      steps: 0,
      hint: 'Kéo trong khung để di chuyển, kéo góc để phóng to thu nhỏ, kéo núm trên để xoay. Giữ Ctrl để xoay tự do. Esc để thoát.',
    };
  }

  overlayBox(context: ToolContext): TransformBox | null {
    const frame = this.frameFor(context);
    if (frame === null) return null;

    // A grip on a selection nothing can move is a promise the tool cannot keep.
    if (movableRootsOf(context.document, context.selection).length === 0) return null;

    return boxOf(frame, geometryFor(context.zoom), this.handlesFor(context), this.readout);
  }

  /** The live figure beside the pointer, held here so it survives between pointer events. */
  private readout: { readonly at: Vec2; readonly text: string } | null = null;

  /* ------------------------------------------------------------------- internals */

  /** Which grip a press at `world` takes, or `null` for a press outside the frame. */
  private gripAt(context: ToolContext, frame: TransformFrame, world: Vec2): Grip | null {
    const handle = hitHandle(frame, world, geometryFor(context.zoom), this.handlesFor(context));
    if (handle !== null) return handle;
    return containsPoint(frame, world) ? 'body' : null;
  }

  /**
   * Which grips are on offer.
   *
   * Edge grips appear only where a per-axis stretch is genuinely representable — one free shape
   * that stores its two axes separately. Everywhere else the corners resize uniformly and there
   * is no edge grip at all, because an edge grip that could only manage a uniform resize would
   * be four handles promising something the drawing cannot express.
   */
  private handlesFor(context: ToolContext): readonly Handle[] {
    return this.canReshape(context) ? RESHAPE_HANDLES : SIMILARITY_HANDLES;
  }

  private canReshape(context: ToolContext): boolean {
    return reshapableRootOf(context.document, context.selection) !== null;
  }

  /** What a drag from this grip will mean. Decided once, at press. */
  private gestureFor(context: ToolContext, grip: Grip): Gesture {
    if (grip === 'body') return 'move';
    if (grip === Handle.Rotate) return 'similarity';
    return this.canReshape(context) ? 'reshape' : 'similarity';
  }

  /**
   * The entities a gesture will rewrite, as they are now.
   *
   * Whole entities rather than sources, so `cancel` can hand them straight back.
   */
  private snapshot(context: ToolContext): ReadonlyMap<EntityId, Entity> | null {
    const roots = movableRootsOf(context.document, context.selection);
    if (roots.length === 0) return null;

    const before = new Map<EntityId, Entity>();
    for (const id of roots) {
      const entity = context.document.entities.get(id);
      if (entity !== undefined) before.set(id, entity);
    }
    return before.size === 0 ? null : before;
  }

  /**
   * What the drag means, or `null` when it means nothing this frame.
   *
   * `shift` locks a stretch's two factors together — the habit every drawing program has taught,
   * and the one modifier a user reaching for this feature already knows. It cannot be `alt`,
   * which the controller intercepts before a tool ever sees it.
   */
  private transformFor(state: Gripped, at: Vec2, input: PointerInput): Similarity | Reshape | null {
    switch (state.gesture) {
      case 'move':
        return translation(vec.subtract(at, state.origin));

      case 'similarity':
        if (state.grip === 'body') return null;
        return similarityFromDrag(state.frame, state.grip, state.origin, at, {
          freeRotation: input.modifiers.primary,
        });

      case 'reshape':
        if (state.grip === 'body') return null;
        return reshapeFromDrag(state.frame, state.grip, state.origin, at, {
          uniform: input.modifiers.shift,
        });
    }
  }

  /**
   * Where the grip should be taken to be, once snapping has had its say.
   *
   * ## Why only a resize, and only when the shape is not moving under it
   *
   * `SelectTool` deliberately does not snap a multi-entity move, and the argument is sound:
   * snapping asks *which existing point is this one near*, which is a sensible question for one
   * point and a meaningless one for a figure of twenty. A grip is one point, so the question is
   * sensible again — and it is the difference between an ellipse that looks like it fits a
   * rectangle and one that does.
   *
   * A **move** still gets none, for the original reason: it is the whole figure that is being
   * placed, not a point. A **turn** gets none either, because the fifteen-degree snap is already
   * answering a better question about it, and two snaps would fight.
   *
   * The entities under the gesture are excluded, or the grip would snap to the very shape it is
   * dragging and the drag would seize on its own outline.
   */
  private snapped(context: ToolContext, state: Gripped, input: PointerInput): SnapResult | null {
    if (state.gesture === 'move' || state.grip === Handle.Rotate) return null;
    // The editor's standing escape from snapping, and this obeys it like everything else.
    if (input.modifiers.primary) return null;

    const result = snap(
      {
        scene: context.scene,
        zoom: context.zoom,
        exclude: new Set(state.before.keys()),
        settings: context.snapSettings,
      },
      input.world
    );

    return result.kind === 'none' ? null : result;
  }

  /**
   * The edits a drag to here produces.
   *
   * Every move re-derives from the snapshot rather than nudging the current sources, so where
   * the figure lands depends only on where the pointer is — not on the path it took, and not on
   * how many events the machine delivered. Dropping the gesture and repeating it lands in
   * exactly the same place.
   */
  private gestureResponse(context: ToolContext, input: PointerInput): ToolResponse {
    if (this.state.name !== 'gesturing') return {};

    const marker = this.snapped(context, this.state, input);
    const at = marker?.world ?? input.world;

    const transform = this.transformFor(this.state, at, input);
    if (transform === null) return { cursor: 'grabbing', snap: marker ?? undefined };

    const restored = { ...context.document, entities: new Map(context.document.entities) };
    for (const [id, entity] of this.state.before) restored.entities.set(id, entity);

    const ids = this.state.before.keys();
    const actions =
      this.state.gesture === 'reshape'
        ? reshapeActions(restored, ids, transform as Reshape)
        : transformActions(restored, ids, transform as Similarity);

    // Empty means either "nothing to do" or "one of these cannot express it". Both say the same
    // thing to the user: this drag changes nothing, and the drawing is left exactly alone.
    if (actions.length === 0) return { cursor: 'grabbing' };

    this.readout = readoutFor(this.state, transform, at);

    return {
      actions,
      snap: marker ?? undefined,
      cursor: 'grabbing',
      label: LABELS[this.state.gesture === 'move' ? 'body' : this.state.grip] ?? 'Resize selection',
      mergeKey: this.state.mergeKey,
    };
  }
}

/** Undo-menu wording, so a history list says which gesture it was. */
const LABELS: Readonly<Record<string, string>> = {
  body: 'Move selection',
  rotate: 'Rotate selection',
};

/**
 * What the gesture is currently producing, for the figure beside the cursor.
 *
 * ## Why a turn reports where the figure has *landed*, not how far it moved
 *
 * Because that is what the snap acts on. Snapping the amount would round a drag of 43° to 45°
 * and leave a frame already sitting at 7° pointing at 52°, which is no rounder than where it
 * started; what a user wants is the figure square to the page, and that is a statement about
 * where it ends up. Reporting the amount while snapping the result would then print `83°` at
 * the exact moment the gesture produced a clean right angle, and make the snap look broken.
 *
 * From upright — which is where a figure almost always starts — the two numbers are the same.
 *
 * The reading is unsigned past half a turn, so a figure turned the short way anticlockwise says
 * `-30°` rather than `330°`.
 *
 * A stretch reports the **size it is producing** rather than a pair of factors. Two factors are
 * what the arithmetic works in; a width and a height are what a user is trying to hit, and this
 * is the only place the editor can tell them whether they have hit it.
 *
 * A move gets no figure at all. The pointer is already showing it, and a number that repeats
 * what the hand is doing is furniture.
 */
function readoutFor(
  state: Gripped,
  transform: Similarity | Reshape,
  at: Vec2
): { readonly at: Vec2; readonly text: string } | null {
  if (state.gesture === 'move') return null;

  if (state.gesture === 'reshape') {
    const { scaleX, scaleY } = transform as Reshape;
    const width = state.frame.width * Math.abs(scaleX);
    const height = state.frame.height * Math.abs(scaleY);
    return { at, text: `${round(width)} × ${round(height)}` };
  }

  const similarity = transform as Similarity;

  if (state.grip === 'rotate') {
    const turned = headingOf(frameAfter(state.frame, similarity));
    const degrees = angle.toDegrees(angle.normalizeSigned(turned));
    // One decimal only when the drag is off a whole degree, so a snapped turn reads `90°`.
    const places = Math.abs(degrees - Math.round(degrees)) < 5e-3 ? 0 : 1;
    return { at, text: `${degrees.toFixed(places)}°` };
  }

  return { at, text: `×${similarity.scale.toFixed(2)}` };
}

/** A world length, to the nearest tenth, without a trailing `.0` on a whole number. */
function round(value: number): string {
  return Number(value.toFixed(1)).toString();
}

/** Identity of a selection, for noticing when the frame belongs to a different one. */
function selectionKey(selection: Iterable<EntityId>): string {
  return [...selection].sort().join(',');
}
